import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, channelSecrets, streamName, type Channel, type CreatedChannel, type Stream } from '../api';
import { CopyField, Icon, StatusBadge, clock, endReason, recordingLine, useNow, when } from '../ui';
import { ChannelBadge } from './Channels';

/** "2026-10-11T08:00" for a datetime-local input, in the browser's time zone. */
function localInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function ChannelPage() {
  const { id } = useParams({ from: '/app/channels/$id' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const now = useNow(1000);
  const q = useQuery({ queryKey: ['channel', id], queryFn: () => api<Channel>(`/v1/channels/${id}`), refetchInterval: 5_000 });
  const channelStreams = useQuery({
    queryKey: ['streams', 'channel', id],
    queryFn: () => api<{ data: Stream[]; has_more: boolean }>(`/v1/streams?channel_id=${id}&limit=20`),
    refetchInterval: 10_000,
  });
  const [secrets, setSecrets] = useState(() => channelSecrets.get(id) ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<string | null>(null);

  if (q.isPending) return <section className="page"><p className="muted">Loading…</p></section>;
  if (q.error || !q.data) return <section className="page"><p className="error-text">{q.error?.message ?? 'Not found.'}</p></section>;
  const c = q.data;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['channel', id] });
    await qc.invalidateQueries({ queryKey: ['channels'] });
  };
  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setActionError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const rotate = () => {
    if (!window.confirm('Replace the key? The old one stops working for new connections at once. A stream on air keeps going, but if it drops, the encoder needs the new key.')) return;
    void act('rotate', async () => {
      const r = await api<CreatedChannel>(`/v1/channels/${id}/stream-key`, { method: 'POST' });
      channelSecrets.set(id, { stream_key: r.stream_key, whip_url: r.whip_url });
      setSecrets(channelSecrets.get(id)!);
    });
  };
  const toggle = () =>
    act('status', () => api(`/v1/channels/${id}`, { method: 'PATCH', json: { status: c.status === 'active' ? 'disabled' : 'active' } }));
  const remove = () => {
    if (!window.confirm(`Delete ${c.name ?? 'this channel'}? The key stops working${c.live_stream_id ? ' and the stream on air ends' : ''}. Its past streams and recordings stay.`)) return;
    void act('delete', async () => {
      await api(`/v1/channels/${id}`, { method: 'DELETE' });
      channelSecrets.delete(id);
      navigate({ to: '/channels' });
    });
  };
  const saveSchedule = () =>
    act('schedule', async () => {
      await api(`/v1/channels/${id}`, { method: 'PATCH', json: { scheduled_at: schedule ? new Date(schedule).toISOString() : null } });
      setSchedule(null);
    });
  const embed = `<iframe src="${c.embed_url}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen style="aspect-ratio:16/9;width:100%;border:0"></iframe>`;
  const rows = channelStreams.data?.data ?? [];

  return (
    <section className="page">
      <div className="crumbs">
        <Link to="/channels">Channels</Link>
        <span aria-hidden="true">/</span>
        <span>{c.name ?? c.id}</span>
      </div>
      <div className="page-head">
        <div>
          <h1 className="row" style={{ gap: 12 }}>
            {c.name ?? 'Untitled channel'} <ChannelBadge c={c} />
          </h1>
          <p className="sub mono">{c.id}</p>
        </div>
        <div className="row">
          {c.live_stream_id && (
            <Link to="/streams/$id" params={{ id: c.live_stream_id }} className="btn primary">
              Open the live stream
            </Link>
          )}
          <button type="button" className="btn" onClick={toggle} disabled={!!busy}>
            {c.status === 'active' ? 'Disable' : 'Enable'}
          </button>
          <button type="button" className="btn danger" onClick={remove} disabled={!!busy}>
            Delete
          </button>
        </div>
      </div>
      {actionError && <p className="error-text" role="alert">{actionError}</p>}
      {c.status === 'disabled' && <p className="muted">Disabled: the key is refused and the player page says unavailable.</p>}

      {secrets && (
        <section className="card secret-panel" aria-labelledby="h-secret">
          <div className="card-head" style={{ alignItems: 'flex-start' }}>
            <div className="row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'nowrap' }}>
              <span style={{ color: 'var(--warn)', marginTop: 2 }}>
                <Icon name="lock" size={18} />
              </span>
              <div>
                <h2 id="h-secret" style={{ fontSize: 16 }}>Copy the channel key now</h2>
                <p className="muted" style={{ margin: '4px 0 0' }}>
                  It is shown only this once. Save it in your encoder: it works for every stream of this channel until you replace it.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                channelSecrets.delete(id);
                setSecrets(null);
              }}
            >
              I've saved it
            </button>
          </div>
          <CopyField label="RTMP server" value={c.rtmp_url} oneLine />
          <CopyField label="Stream key" value={secrets.stream_key} oneLine secret />
          <CopyField label="WHIP URL" value={secrets.whip_url} oneLine secret tip="For publishing from a browser or any WHIP client. It contains the key." />
        </section>
      )}

      <div className="stream-grid">
        <div className="col">
          <section className="card" aria-labelledby="h-ingest">
            <div className="card-head">
              <h2 id="h-ingest">Encoder</h2>
              <button type="button" className="btn small" onClick={rotate} disabled={!!busy}>
                Replace key
              </button>
            </div>
            <CopyField label="RTMP server" value={c.rtmp_url} oneLine tip="In OBS: Settings → Stream → Custom. The key is shown once, when the channel is made or the key replaced." />
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {c.stream_key_rotated_at ? `Key replaced ${when(c.stream_key_rotated_at, now)}.` : 'The key from when the channel was made.'} A dropped encoder that comes back within 60 s continues
              the same stream.
            </p>
          </section>

          <section className="card" aria-labelledby="h-player">
            <div className="card-head">
              <h2 id="h-player">Player</h2>
              <a className="btn small" href={c.embed_url} target="_blank" rel="noopener">
                Open <Icon name="external" size={12} />
              </a>
            </div>
            <CopyField label="Embed code" value={embed} tip="One player for all of this channel’s streams: the one on air, or a waiting page between them." />
            <CopyField label="Player link" value={c.embed_url} oneLine />
          </section>
        </div>

        <div className="col">
          <section className="card" aria-labelledby="h-next">
            <div className="card-head">
              <h2 id="h-next">Next stream</h2>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Shown on the waiting page. It reserves nothing.</p>
            <div className="row">
              <input
                className="input"
                type="datetime-local"
                value={schedule ?? localInput(c.scheduled_at)}
                onChange={(e) => setSchedule(e.target.value)}
                aria-label="Next stream"
              />
              <button type="button" className="btn small" onClick={saveSchedule} disabled={schedule === null || !!busy}>
                Save
              </button>
              {c.scheduled_at && schedule === null && (
                <button type="button" className="btn small" onClick={() => setSchedule('')}>
                  Clear
                </button>
              )}
            </div>
          </section>

          <section className="card" aria-labelledby="h-defaults">
            <div className="card-head">
              <h2 id="h-defaults">Every stream starts with</h2>
            </div>
            <ul className="muted" style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
              <li>{c.profile === 'passthrough' ? 'Passthrough' : 'Adaptive qualities'}, up to {c.defaults.max_resolution ?? 720}p</li>
              <li>{c.defaults.record === false ? 'No recording' : `Recording, kept ${c.defaults.recording_retention_days ? `${c.defaults.recording_retention_days} days` : 'for the account default'}`}</li>
              <li>{c.defaults.replay ? 'Replay on: the newest stream plays between streams' : 'No replay'}</li>
              <li>{c.defaults.chat?.enabled ? 'Chat on' : 'Chat off'}</li>
            </ul>
            <p className="faint" style={{ margin: 0, fontSize: 12 }}>Change them with the API: PATCH /v1/channels/{'{id}'} (defaults).</p>
          </section>
        </div>
      </div>

      <section aria-labelledby="h-streams">
        <h2 id="h-streams" style={{ fontSize: 16, margin: '8px 0' }}>Streams</h2>
        {rows.length === 0 ? (
          <p className="muted">None yet. The first one starts when the key connects.</p>
        ) : (
          <div className="table channel-streams" role="table" aria-label="Streams">
            <div className="tr th" role="row">
              <span role="columnheader">Stream</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Started</span>
              <span role="columnheader">Duration</span>
              <span role="columnheader">Recording</span>
            </div>
            {rows.map((s) => {
              const dur = s.went_live_at ? clock(((s.ended_at ? new Date(s.ended_at).getTime() : now) - new Date(s.went_live_at).getTime()) / 1000) : '—';
              return (
                <Link key={s.id} to="/streams/$id" params={{ id: s.id }} className="tr" role="row">
                  <span className="name" role="cell">
                  <b>{streamName(s)}</b>
                  <span>{s.id}</span>
                  </span>
                  <span className="st" role="cell">
                    <StatusBadge status={s.status} />
                    <span>{s.status === 'live' ? 'On air' : endReason(s.end_reason)}</span>
                  </span>
                  <span className="muted" role="cell">{when(s.went_live_at, now)}</span>
                  <span className="mono" role="cell">{dur}</span>
                  <span className="muted" role="cell">{recordingLine(s, now)}</span>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </section>
  );
}
